import { managedFetch } from 'query/fetch';
import React, { useEffect, useState, useCallback, useRef, Suspense, lazy } from "react";
import { ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { setAuthorization } from "query/auth";
import { loadAppReducers } from "./store";
import { logout } from "helpers/backend_helper";
import { subscribeToMaintenance, unsubscribeFromMaintenance } from "./services/realtimeMaintenance";
import useCheckVersion from "Components/Co2Common/Hooks/useCheckVersion";
import { useModalBodyScrollFix } from "Components/Co2Common/Hooks/useModalBodyScrollFix";
import GlobalDraggableModals from "Components/Co2Common/GlobalDraggableModals";
import Co2TrackedLoader from "Components/Co2Common/Co2TrackedLoader";
import VideoPreviewWindowHost from "Components/Co2Common/Modals/VideoPreviewWindowHost";
import { useTrialAccount } from "hooks/useTrialAccount";
import { useModal } from "Components/Co2ModalService/ModalContext";
import { usePurchaseLicense } from "pages/Co2Table/Actions/Custom/usePurchaseLicense";
import { useDemoAccountGuard } from "hooks/useDemoAccountGuard";
import { resetPosthog } from "services/posthog";

// Load only toastify CSS synchronously
import "react-toastify/dist/ReactToastify.css";

// Load full theme CSS synchronously (we need it for the app to look correct)
// The optimization is still in effect because of code splitting and lazy loading
import "./assets/scss/themes.scss";

// Lazy load heavy components
const ModalProvider = lazy(() =>
	import("Components/Co2ModalService/ModalContext").then((m) => ({
		default: m.ModalProvider,
	})),
);
const ModalContainer = lazy(
	() => import("Components/Co2ModalService/ModalContainer"),
);
const ExpiredLicenseModal = lazy(
	() => import("Components/Co2Common/Modals/ExpiredLicenseModal"),
);
const TopUpTokensModal = lazy(
	() => import("Components/Co2Common/Modals/TopUpTokensModal"),
);
const ModalMessageBox = lazy(
	() => import("Components/Co2Common/Modals/ModalMessageBox"),
);
const MaintenanceOverlay = lazy(
	() => import("Components/Co2Common/MaintenanceOverlay"),
);

// Import Firebase Configuration file
// import { initFirebaseBackend } from "./helpers/firebase_helper";

// Lazy load Route to allow code splitting
const Route = lazy(() => import("./Routes"));

// Rendered inside <ModalProvider> (App() itself sits above that provider, so it can't
// call useDemoAccountGuard/useModal directly). Trial accounts purchase a license;
// paid accounts keep the top-up flow. Demo accounts cannot purchase either.
type InsufficientTokens = { message: string; retry: () => void; cancel: () => void };

const InsufficientTokensFlow = ({
	insufficientTokens,
	tokensStep,
	setTokensStep,
	setInsufficientTokens,
}: {
	insufficientTokens: InsufficientTokens | null;
	tokensStep: "confirm" | "topup" | null;
	setTokensStep: (step: "confirm" | "topup" | null) => void;
	setInsufficientTokens: (value: InsufficientTokens | null) => void;
}) => {
	const { denyIfDemoAccount } = useDemoAccountGuard();
	const { isTrialAccount } = useTrialAccount();
	const { showModal } = useModal();
	const { openPurchaseLicense, purchaseLicenseModals } = usePurchaseLicense({ showModal });

	const dismiss = () => {
		insufficientTokens?.cancel();
		setInsufficientTokens(null);
		setTokensStep(null);
	};

	// Keep the purchase modal mounted after cancelling the blocked token request.
	if (!insufficientTokens) return purchaseLicenseModals;

	return (
		<>
			{purchaseLicenseModals}
			{tokensStep === "confirm" && (
				<Suspense fallback={null}>
					<ModalMessageBox
						open={true}
						type="warning"
						toggle={() => setTokensStep(null)}
						title={isTrialAccount ? "Purchase license" : "Insufficient tokens"}
						description={isTrialAccount ? "Purchase a license to continue using this feature." : insufficientTokens.message}
						question={isTrialAccount ? "Would you like to purchase a license now?" : "Would you like to top up your token balance now?"}
						onConfirm={() => {
							if (denyIfDemoAccount()) {
								dismiss();
								return;
							}
							if (isTrialAccount) {
								void openPurchaseLicense();
								dismiss();
								return;
							}
							setTokensStep("topup");
						}}
						onClose={dismiss}
					/>
				</Suspense>
			)}
			{tokensStep === "topup" && !isTrialAccount && (
				<Suspense fallback={null}>
					<TopUpTokensModal
						open={true}
						onClose={dismiss}
						onSuccess={() => {
							insufficientTokens.retry();
							setInsufficientTokens(null);
							setTokensStep(null);
						}}
					/>
				</Suspense>
			)}
		</>
	);
};

function App() {
	const location = useLocation();
	const navigate = useNavigate();
	const [themeLoaded, setThemeLoaded] = useState(false);
	const [reducersLoaded, setReducersLoaded] = useState(false);
	const [licenseExpired, setLicenseExpired] = useState<{
		show: boolean;
		isAdmin: boolean;
		isTrial: boolean;
		paymentFailed: boolean;
		message: string;
		renewalUrl: string;
	} | null>(null);
	const [maintenanceActive, setMaintenanceActive] = useState<{
		active: boolean;
		reason: string | null;
		suspendTo: string;
		suspensionType: "full" | "restricted";
	} | null>(null);
	const [insufficientTokens, setInsufficientTokens] = useState<{
		message: string;
		retry: () => void;
		cancel: () => void;
	} | null>(null);
	// 'confirm' shows the "do you want to top up?" prompt first; 'topup' shows the
	// actual plan-picker modal once the user says yes.
	const [tokensStep, setTokensStep] = useState<"confirm" | "topup" | null>(null);
	const maintenanceKickoutTriggeredRef = useRef(false);

	// Check if we're on an auth page
	const isAuthPage =
		location.pathname.startsWith("/login") ||
		location.pathname.startsWith("/register") ||
		location.pathname.startsWith("/logout");

	useCheckVersion();
	useModalBodyScrollFix();

	// Handle license expiration events
	useEffect(() => {
		const handleLicenseExpired = (event: CustomEvent) => {
			setLicenseExpired({
				show: true,
				isAdmin: event.detail.isAdmin,
				isTrial: !!event.detail.isTrial,
				paymentFailed: event.detail.paymentFailed,
				message: event.detail.message,
				renewalUrl: event.detail.renewalUrl || "/renew-license",
			});
		};

		window.addEventListener(
			"license:expired",
			handleLicenseExpired as EventListener,
		);
		return () => {
			window.removeEventListener(
				"license:expired",
				handleLicenseExpired as EventListener,
			);
		};
	}, []);


	// Handle insufficient-token-balance events (402 INSUFFICIENT_TOKENS, dispatched by
	// the requests response interceptor in helpers/api_helper.ts) — first asks whether the
	// user wants to top up, then shows the top-up modal in place of whatever form/modal
	// triggered the blocked request, and resumes that exact request afterwards via the
	// retry() callback, so the user never loses what they'd already filled in and never
	// sees a raw 402 error.
	useEffect(() => {
		const handleInsufficientTokens = (event: CustomEvent) => {
			setInsufficientTokens({
				message: event.detail.message,
				retry: event.detail.retry,
				cancel: event.detail.cancel,
			});
			setTokensStep("confirm");
		};

		window.addEventListener(
			"tokens:insufficient",
			handleInsufficientTokens as EventListener,
		);
		return () => {
			window.removeEventListener(
				"tokens:insufficient",
				handleInsufficientTokens as EventListener,
			);
		};
	}, []);

	const handleLicenseLogout = useCallback(async () => {
		setLicenseExpired(null);
		try {
			await logout();
		} catch (err) {
			// ignore API errors; still clear local session
		} finally {
			resetPosthog();
			sessionStorage.removeItem("authUser");
			sessionStorage.removeItem("homePage");
			navigate("/login");
		}
	}, [navigate]);

		// Handle maintenance mode events (from realtime + 503 interceptor)
		useEffect(() => {
			const activateMaintenance = (detail: any) => {
				const suspensionType = detail?.suspension_type || "full";
				const eventSource = detail?.source || "global";
				const hasSession = Boolean(sessionStorage.getItem("authUser"));
				const isUserSpecificRestrictedBlock =
					suspensionType === "restricted" && eventSource === "http-503";

				// Restricted maintenance is user-targeted. Do not show the blocking overlay
				// for global status/realtime events because they are not user-specific.
				if (suspensionType === "restricted" && !isUserSpecificRestrictedBlock) {
					return;
				}

				setMaintenanceActive({
					active: true,
					reason: detail?.reason || null,
					suspendTo: detail?.suspend_to || "",
					suspensionType,
				});

				if (maintenanceKickoutTriggeredRef.current) {
					return;
				}

				if (!hasSession) {
					return;
				}

				if (suspensionType !== "full" && !isUserSpecificRestrictedBlock) {
					return;
				}

				maintenanceKickoutTriggeredRef.current = true;

			resetPosthog();
			sessionStorage.removeItem("authUser");
			sessionStorage.removeItem("homePage");
			setAuthorization(null);

			// Hard reload ensures all in-memory auth/realtime state is torn down immediately.
			window.location.replace("/login");
		};

		const handleMaintenanceActivated = (event: CustomEvent) => {
			activateMaintenance(event.detail);
		};

		const handleMaintenanceEnded = () => {
			maintenanceKickoutTriggeredRef.current = false;
			setMaintenanceActive(null);
		};

		window.addEventListener(
			"maintenance:activated",
			handleMaintenanceActivated as EventListener,
		);
		window.addEventListener(
			"maintenance:ended",
			handleMaintenanceEnded as EventListener,
		);

		return () => {
			window.removeEventListener(
				"maintenance:activated",
				handleMaintenanceActivated as EventListener,
			);
			window.removeEventListener(
				"maintenance:ended",
				handleMaintenanceEnded as EventListener,
			);
		};
	}, []);

	// Initialize realtime maintenance subscription
	useEffect(() => {
		subscribeToMaintenance((payload) => {
			console.log("[App] Maintenance event received:", payload);
		});

		return () => {
			unsubscribeFromMaintenance();
		};
	}, []);

	// Fetch initial maintenance state on page load
	// Uses managed native fetch to avoid triggering the 503 interceptor
	useEffect(() => {
		const fetchMaintenanceStatus = async () => {
				try {
					const baseUrl = import.meta.env.VITE_API_URL || "";
					const data = await managedFetch(`${baseUrl}/api/maintenance/status`, {
						credentials: "omit",
					}, async response => response.ok ? response.json() : null);
					if (!data) return;
					if (data.active) {
						window.dispatchEvent(
							new CustomEvent("maintenance:activated", {
									detail: {
										suspension_type: data.active.suspension_type || "full",
										reason: data.active.reason || null,
										suspend_to: data.active.suspend_to || "",
										status: data.active.status || "active",
										source: "status",
										event_type: "maintenance:activated",
									},
								}),
							);
					}
				} catch {
					// Silent fail -- realtime and 503 interceptor remain as fallbacks
				}
			};

			fetchMaintenanceStatus();
		}, []);

	// Theme CSS is now loaded synchronously above
	// This ensures no layout issues on any page
	useEffect(() => {
		setThemeLoaded(true);
	}, []);

	// Load all app reducers when navigating away from auth pages
	useEffect(() => {
		if (!isAuthPage && !reducersLoaded) {
			loadAppReducers().then(() => {
				setReducersLoaded(true);
			});
		}
	}, [isAuthPage, reducersLoaded]);

	return (
		<React.Fragment>
			<Suspense
				fallback={
					<Co2TrackedLoader variant="overlay" aria-label="Loading application" />
				}
			>
				<ModalProvider>
					<GlobalDraggableModals />
					<VideoPreviewWindowHost />
					<ToastContainer />
					{/*
						Route gets its own Suspense boundary, isolated from the modals below.
						Lazy modals (ExpiredLicenseModal, ModalMessageBox, TopUpTokensModal,
						MaintenanceOverlay) are conditionally mounted and each load their chunk
						the first time they're shown. If they shared this boundary, the FIRST
						time any one of them suspends, React shows this boundary's fallback for
						the WHOLE subtree — unmounting Route (and any open modal/form under it,
						e.g. an in-progress activity-entry form) and remounting it from scratch
						once the chunk loads, silently wiping whatever the user had filled in.
					*/}
					<Suspense
						fallback={
							<Co2TrackedLoader variant="overlay" aria-label="Loading application" />
						}
					>
						<Route />
					</Suspense>
					<Suspense fallback={null}>
						<ModalContainer />
					</Suspense>
					{licenseExpired?.show && !isAuthPage && !['/renew-license', '/business/licenses'].some(path => location.pathname.startsWith(path)) && (
						<Suspense fallback={null}>
							<ExpiredLicenseModal
								open={licenseExpired.show}
								isAdmin={licenseExpired.isAdmin}
								isTrial={licenseExpired.isTrial}
								paymentFailed={licenseExpired.paymentFailed}
								message={licenseExpired.message}
								renewalUrl={licenseExpired.renewalUrl}
								onLogout={handleLicenseLogout}
								onDismiss={() => setLicenseExpired(null)}
							/>
						</Suspense>
					)}
					<InsufficientTokensFlow
						insufficientTokens={insufficientTokens}
						tokensStep={tokensStep}
						setTokensStep={setTokensStep}
						setInsufficientTokens={setInsufficientTokens}
					/>
					{maintenanceActive?.active && (
						<Suspense fallback={null}>
							<MaintenanceOverlay
								reason={maintenanceActive.reason}
								suspendTo={maintenanceActive.suspendTo}
								suspensionType={maintenanceActive.suspensionType}
								onRecovery={() => setMaintenanceActive(null)}
							/>
						</Suspense>
					)}
				</ModalProvider>
			</Suspense>
		</React.Fragment>
	);
}

export default App;
